import { Capacitor } from "@capacitor/core";
import { deepMerge, use } from "./object";
import type { DeepPartial } from "./types";

export const isNativeBuild = Capacitor?.isNativePlatform();
export const platform = Capacitor?.getPlatform();

/**
 * Resolves the base URL for the API based on environment variables and platform.
 * If running in a native build, it defaults to a specific URL.
 * If running in a web environment, it adjusts based on the current window location.
 *
 * @returns {string} The resolved base URL for the API.
 */
function resolveBaseUrl() {
  function readEnvDirectusURL() {
    try {
      return process.env.BUN_PUBLIC_DIRECTUS_URL;
    } catch {
      return undefined;
    }
  }

  const envDirectusURL = readEnvDirectusURL();

  const apiURL = envDirectusURL
    ? envDirectusURL
    : window.location.port === "1234"
      ? `${window.location.origin.replace(window.location.port, "8055")}`
      : isNativeBuild
        ? "https://im.byagro.dev.br/api"
        : `${window.location.origin}/api`;
  console.log("Resolved API URL:", apiURL, {
    envDirectusURL,
    isNativeBuild,
    platform,
    windowLocation: {
      origin: window.location.origin,
      port: window.location.port,
    },
  });
  return apiURL;
}

export class AppConfig {
  public readonly api = {
    baseUrl: resolveBaseUrl(),
  };
  public readonly auth = {
    verificationUrl: isNativeBuild
      ? "https://im.byagro.dev.br/#/verify-email?native=true"
      : use(new URL(window.location.href), (url) => {
          url.hash = "/verify-email";
          return url.toString();
        }),
    resetUrl: isNativeBuild
      ? "https://im.byagro.dev.br/#/reset-password?native=true"
      : use(new URL(window.location.href), (url) => {
          url.hash = "/reset-password";
          return url.toString();
        }),
  };
  public readonly localWifi = {
    ip: "192.168.4.1",
    username: "byagro",
    password: "i8dEYH7tcNxVf18",
  };
  public readonly irrigation = {
    // Minimum gap between irrigation plans of the same project in minutes.
    // Depends on schedule resumption; see docs/adr/0001-minimum-plan-gap-by-schedule-resumption.md
    planMinimumGapMinutes: {
      withResumption: 30,
      withoutResumption: 5,
    },
  };

  /**
   * Deep-merge a partial config object into this instance.
   * Only defined (non-undefined) values from `partial` are applied.
   * Nested objects are merged recursively; scalar values are overwritten.
   */
  merge(partial: DeepPartial<AppConfig>): void {
    deepMerge(this, partial);
  }

  /**
   * Fetch a JSON config from a URL and deep-merge it into this instance.
   * @throws {Error} If the response is not ok (non-2xx) or the body is not valid JSON.
   */
  async mergeFromUrl(url: string): Promise<void> {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch config from ${url}: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    if (typeof data !== "object" || data === null || Array.isArray(data)) {
      throw new Error(`Invalid config JSON from ${url}: expected a plain object`);
    }
    this.merge(data);
  }
}

export const appConfig = new AppConfig();
console.log("AppConfig initialized:", appConfig);
